import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Ab1482Status,
  ApplianceCategory,
  ApplianceCondition,
  ApplianceCreateInput,
  ApplianceDTO,
  ApplianceUpdateInput,
  CityRegistrationStatus,
  OwnerDTO,
  PropertyCreateInput,
  PropertyDTO,
  PropertyType,
  PropertyUpdateInput,
  UnitCreateInput,
  UnitDTO,
  UnitStatus,
  UnitUpdateInput,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Properties — the portfolio CRUD screen. Mirrors the canonical Tenants pattern: a react-query
 * list + a "New" button, per-row Edit/Delete, and create/edit forms in an accessible {@link Dialog}.
 * Each property row expands into a Units panel (units + their appliances), all managed inline. Money
 * is stored as integer CENTS on the wire; forms take DOLLARS and convert. All writes go through the
 * shared `api` client (CSRF handled there) and invalidate the relevant query on success.
 */

const PROPERTIES_KEY = ['admin', 'properties'] as const;
const OWNERS_KEY = ['admin', 'owners'] as const;
const unitsKeyFor = (propertyId: string) => ['admin', 'units', propertyId] as const;

const PROPERTY_TYPES: PropertyType[] = ['single_family', 'multi_family', 'condo', 'townhouse', 'adu'];
const CITY_REG_STATUSES: CityRegistrationStatus[] = ['not_required', 'pending', 'registered', 'exempt'];
const UNIT_STATUSES: UnitStatus[] = ['vacant', 'occupied', 'make_ready', 'off_market'];
const AB1482_STATUSES: Ab1482Status[] = ['exempt', 'covered', 'unknown'];
const APPLIANCE_CATEGORIES: ApplianceCategory[] = [
  'stove',
  'refrigerator',
  'dishwasher',
  'washer',
  'dryer',
  'microwave',
  'water_heater',
  'hvac',
  'disposal',
  'other',
];
const APPLIANCE_CONDITIONS: ApplianceCondition[] = ['working', 'needs_repair', 'not_working', 'not_present'];

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}
/** Parse an optional non-negative integer field; blank/invalid → null (omitted on the wire). */
function intOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}
/** Parse a required non-negative integer field; blank/invalid → 0. */
function intOrZero(v: string): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}
/** DOLLARS string → integer CENTS; blank/invalid → null. */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
/** Integer CENTS → a DOLLARS string for a form input; null/undefined → "". */
function centsToDollars(cents: number | null | undefined): string {
  return cents == null ? '' : (cents / 100).toFixed(2);
}
/** Baths (e.g. "1.5") → tenths-of-a-bath integer (15). Blank/invalid → 0. */
function bathsToX10(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) : 0;
}
/** Render an enum value as a human label (`single_family` → `single family`). */
function labelize(s: string): string {
  return s.replace(/_/g, ' ');
}

/* ------------------------------------------------------------------ *
 * Property create/edit form.                                         *
 * ------------------------------------------------------------------ */

function PropertyForm({ property, onClose }: { property: PropertyDTO | null; onClose: () => void }) {
  const uid = useId();
  const queryClient = useQueryClient();
  const editing = property != null;
  const [ownerId, setOwnerId] = useState(property?.ownerId ?? '');
  const [name, setName] = useState(property?.name ?? '');
  const [line1, setLine1] = useState(property?.line1 ?? '');
  const [line2, setLine2] = useState(property?.line2 ?? '');
  const [city, setCity] = useState(property?.city ?? 'San Luis Obispo');
  const [state, setState] = useState(property?.state ?? 'CA');
  const [postalCode, setPostalCode] = useState(property?.postalCode ?? '');
  const [type, setType] = useState<PropertyType>(property?.type ?? 'single_family');
  const [yearBuilt, setYearBuilt] = useState(property?.yearBuilt != null ? String(property.yearBuilt) : '');
  const [cityReg, setCityReg] = useState<CityRegistrationStatus>(
    property?.cityRegistrationStatus ?? 'not_required',
  );

  // Owners only feed the create-time <select> (ownerId is not re-assignable on update).
  const ownersQuery = useQuery({
    queryKey: OWNERS_KEY,
    queryFn: () => api.owners.list(),
    enabled: !editing,
  });
  const owners = ownersQuery.data?.owners ?? [];

  const save = useMutation({
    mutationFn: () => {
      if (property) {
        const body: PropertyUpdateInput = {
          name: name.trim(),
          line1: line1.trim(),
          line2: opt(line2) ?? null,
          city: city.trim() || 'San Luis Obispo',
          state: state.trim() || 'CA',
          postalCode: opt(postalCode) ?? null,
          yearBuilt: intOrNull(yearBuilt),
          type,
          cityRegistrationStatus: cityReg,
        };
        return api.properties.update(property.id, body);
      }
      const body: PropertyCreateInput = {
        ownerId,
        name: name.trim(),
        line1: line1.trim(),
        line2: opt(line2) ?? null,
        city: city.trim() || 'San Luis Obispo',
        state: state.trim() || 'CA',
        postalCode: opt(postalCode) ?? null,
        jurisdiction: 'SLO',
        yearBuilt: intOrNull(yearBuilt),
        type,
        cityRegistrationStatus: cityReg,
      };
      return api.properties.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROPERTIES_KEY });
      onClose();
    },
  });

  return (
    <Dialog title={editing ? 'Edit property' : 'New property'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}

        {!editing && (
          <div>
            <label htmlFor={`${uid}-owner`}>Owner</label>
            <select
              id={`${uid}-owner`}
              value={ownerId}
              required
              onChange={(e) => setOwnerId(e.target.value)}
            >
              <option value="">Select an owner…</option>
              {owners.map((o: OwnerDTO) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {ownersQuery.isError && (
              <StatusMessage tone="err">{errorMessage(ownersQuery.error)}</StatusMessage>
            )}
            {!ownersQuery.isLoading && !ownersQuery.isError && owners.length === 0 && (
              <p className="muted">No owners exist yet — add an owner before creating a property.</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor={`${uid}-name`}>Name</label>
          <input
            id={`${uid}-name`}
            value={name}
            required
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Marsh Street Duplex"
          />
        </div>
        <div>
          <label htmlFor={`${uid}-line1`}>Address line 1</label>
          <input
            id={`${uid}-line1`}
            value={line1}
            required
            maxLength={200}
            onChange={(e) => setLine1(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={`${uid}-line2`}>Address line 2 (optional)</label>
          <input
            id={`${uid}-line2`}
            value={line2}
            maxLength={200}
            onChange={(e) => setLine2(e.target.value)}
          />
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-city`}>City</label>
            <input
              id={`${uid}-city`}
              value={city}
              required
              maxLength={200}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div style={{ width: '6rem' }}>
            <label htmlFor={`${uid}-state`}>State</label>
            <input
              id={`${uid}-state`}
              value={state}
              required
              maxLength={64}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
          <div style={{ width: '8rem' }}>
            <label htmlFor={`${uid}-postal`}>Postal code (optional)</label>
            <input
              id={`${uid}-postal`}
              value={postalCode}
              maxLength={16}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-type`}>Type</label>
            <select
              id={`${uid}-type`}
              value={type}
              onChange={(e) => setType(e.target.value as PropertyType)}
            >
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {labelize(t)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '8rem' }}>
            <label htmlFor={`${uid}-year`}>Year built (optional)</label>
            <input
              id={`${uid}-year`}
              type="number"
              min={1800}
              max={2200}
              value={yearBuilt}
              onChange={(e) => setYearBuilt(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label htmlFor={`${uid}-cityreg`}>City registration status</label>
          <select
            id={`${uid}-cityreg`}
            value={cityReg}
            onChange={(e) => setCityReg(e.target.value as CityRegistrationStatus)}
          >
            {CITY_REG_STATUSES.map((s) => (
              <option key={s} value={s}>
                {labelize(s)}
              </option>
            ))}
          </select>
        </div>

        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create property'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Unit create/edit form (nested under a property).                   *
 * ------------------------------------------------------------------ */

function UnitForm({
  propertyId,
  unit,
  onClose,
}: {
  propertyId: string;
  unit: UnitDTO | null;
  onClose: () => void;
}) {
  const uid = useId();
  const queryClient = useQueryClient();
  const editing = unit != null;
  const [label, setLabel] = useState(unit?.label ?? '');
  const [beds, setBeds] = useState(unit != null ? String(unit.beds) : '0');
  const [baths, setBaths] = useState(unit != null ? String(unit.bathsX10 / 10) : '0');
  const [sqft, setSqft] = useState(unit?.sqft != null ? String(unit.sqft) : '');
  const [marketRent, setMarketRent] = useState(centsToDollars(unit?.marketRentCents));
  const [deposit, setDeposit] = useState(centsToDollars(unit?.depositCents));
  const [status, setStatus] = useState<UnitStatus>(unit?.status ?? 'vacant');
  const [ab1482Status, setAb1482Status] = useState<Ab1482Status>(unit?.ab1482Status ?? 'unknown');
  const [notes, setNotes] = useState(unit?.notes ?? '');

  const save = useMutation({
    mutationFn: () => {
      if (unit) {
        const body: UnitUpdateInput = {
          label: label.trim(),
          beds: intOrZero(beds),
          bathsX10: bathsToX10(baths),
          sqft: intOrNull(sqft),
          marketRentCents: dollarsToCents(marketRent),
          depositCents: dollarsToCents(deposit),
          status,
          ab1482Status,
          notes: opt(notes) ?? null,
        };
        return api.units.update(propertyId, unit.id, body);
      }
      const body: UnitCreateInput = {
        label: label.trim(),
        beds: intOrZero(beds),
        bathsX10: bathsToX10(baths),
        sqft: intOrNull(sqft),
        marketRentCents: dollarsToCents(marketRent),
        depositCents: dollarsToCents(deposit),
        status,
        ab1482Status,
        exemptionNoticeServed: false,
        hasSecondUnitAdu: false,
        cityRegistrationStatus: 'not_required',
        notes: opt(notes) ?? null,
      };
      return api.units.create(propertyId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: unitsKeyFor(propertyId) });
      // Unit count changed → the property list's unitCount badge is stale.
      void queryClient.invalidateQueries({ queryKey: PROPERTIES_KEY });
      onClose();
    },
  });

  return (
    <Dialog title={editing ? 'Edit unit' : 'New unit'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor={`${uid}-label`}>Label</label>
          <input
            id={`${uid}-label`}
            value={label}
            required
            maxLength={200}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Unit A"
          />
        </div>
        <div className="row">
          <div style={{ width: '6rem' }}>
            <label htmlFor={`${uid}-beds`}>Beds</label>
            <input
              id={`${uid}-beds`}
              type="number"
              min={0}
              step={1}
              value={beds}
              onChange={(e) => setBeds(e.target.value)}
            />
          </div>
          <div style={{ width: '6rem' }}>
            <label htmlFor={`${uid}-baths`}>Baths</label>
            <input
              id={`${uid}-baths`}
              type="number"
              min={0}
              step={0.5}
              value={baths}
              onChange={(e) => setBaths(e.target.value)}
            />
          </div>
          <div style={{ width: '8rem' }}>
            <label htmlFor={`${uid}-sqft`}>Sq ft (optional)</label>
            <input
              id={`${uid}-sqft`}
              type="number"
              min={0}
              step={1}
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-rent`}>Market rent (USD/mo, optional)</label>
            <input
              id={`${uid}-rent`}
              type="number"
              min={0}
              step={0.01}
              value={marketRent}
              onChange={(e) => setMarketRent(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-deposit`}>Deposit (USD, optional)</label>
            <input
              id={`${uid}-deposit`}
              type="number"
              min={0}
              step={0.01}
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-status`}>Status</label>
            <select
              id={`${uid}-status`}
              value={status}
              onChange={(e) => setStatus(e.target.value as UnitStatus)}
            >
              {UNIT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {labelize(s)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-ab1482`}>AB 1482 status</label>
            <select
              id={`${uid}-ab1482`}
              value={ab1482Status}
              onChange={(e) => setAb1482Status(e.target.value as Ab1482Status)}
            >
              {AB1482_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {labelize(s)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor={`${uid}-notes`}>Notes (optional)</label>
          <textarea
            id={`${uid}-notes`}
            value={notes}
            rows={3}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create unit'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Appliance create/edit form (nested under a unit).                  *
 * ------------------------------------------------------------------ */

function ApplianceForm({
  propertyId,
  unitId,
  appliance,
  onClose,
}: {
  propertyId: string;
  unitId: string;
  appliance: ApplianceDTO | null;
  onClose: () => void;
}) {
  const uid = useId();
  const queryClient = useQueryClient();
  const editing = appliance != null;
  const [category, setCategory] = useState<ApplianceCategory>(appliance?.category ?? 'stove');
  const [make, setMake] = useState(appliance?.make ?? '');
  const [model, setModel] = useState(appliance?.model ?? '');
  const [serial, setSerial] = useState(appliance?.serial ?? '');
  const [condition, setCondition] = useState<ApplianceCondition>(appliance?.condition ?? 'working');
  const [isHabitabilityRequired, setIsHabitabilityRequired] = useState(
    appliance?.isHabitabilityRequired ?? false,
  );
  const [notes, setNotes] = useState(appliance?.notes ?? '');

  const save = useMutation({
    mutationFn: () => {
      if (appliance) {
        const body: ApplianceUpdateInput = {
          category,
          make: opt(make) ?? null,
          model: opt(model) ?? null,
          serial: opt(serial) ?? null,
          condition,
          isHabitabilityRequired,
          notes: opt(notes) ?? null,
        };
        return api.units.updateAppliance(propertyId, unitId, appliance.id, body);
      }
      const body: ApplianceCreateInput = {
        category,
        make: opt(make) ?? null,
        model: opt(model) ?? null,
        serial: opt(serial) ?? null,
        condition,
        isHabitabilityRequired,
        notes: opt(notes) ?? null,
      };
      return api.units.addAppliance(propertyId, unitId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: unitsKeyFor(propertyId) });
      onClose();
    },
  });

  return (
    <Dialog title={editing ? 'Edit appliance' : 'New appliance'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-category`}>Category</label>
            <select
              id={`${uid}-category`}
              value={category}
              onChange={(e) => setCategory(e.target.value as ApplianceCategory)}
            >
              {APPLIANCE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {labelize(c)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-condition`}>Condition</label>
            <select
              id={`${uid}-condition`}
              value={condition}
              onChange={(e) => setCondition(e.target.value as ApplianceCondition)}
            >
              {APPLIANCE_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {labelize(c)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-make`}>Make (optional)</label>
            <input
              id={`${uid}-make`}
              value={make}
              maxLength={200}
              onChange={(e) => setMake(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-model`}>Model (optional)</label>
            <input
              id={`${uid}-model`}
              value={model}
              maxLength={200}
              onChange={(e) => setModel(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label htmlFor={`${uid}-serial`}>Serial (optional)</label>
          <input
            id={`${uid}-serial`}
            value={serial}
            maxLength={200}
            onChange={(e) => setSerial(e.target.value)}
          />
        </div>
        <div className="row">
          <input
            id={`${uid}-habit`}
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isHabitabilityRequired}
            onChange={(e) => setIsHabitabilityRequired(e.target.checked)}
          />
          <label htmlFor={`${uid}-habit`} style={{ margin: 0 }}>
            Required for habitability
          </label>
        </div>
        <div>
          <label htmlFor={`${uid}-anotes`}>Notes (optional)</label>
          <textarea
            id={`${uid}-anotes`}
            value={notes}
            rows={3}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add appliance'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Units panel — the drill-in for one property (units + appliances).  *
 * ------------------------------------------------------------------ */

function UnitsPanel({ propertyId }: { propertyId: string }) {
  const queryClient = useQueryClient();
  const unitsKey = unitsKeyFor(propertyId);
  const query = useQuery({ queryKey: unitsKey, queryFn: () => api.units.list(propertyId) });

  // `undefined` = closed; `null` = create; a unit = edit.
  const [unitEditing, setUnitEditing] = useState<UnitDTO | null | undefined>(undefined);
  // `undefined` = closed; otherwise the target unit + (null = create | appliance = edit).
  const [applianceEditing, setApplianceEditing] = useState<
    { unitId: string; appliance: ApplianceDTO | null } | undefined
  >(undefined);

  const removeUnit = useMutation({
    mutationFn: (u: UnitDTO) => api.units.remove(propertyId, u.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: unitsKey });
      void queryClient.invalidateQueries({ queryKey: PROPERTIES_KEY });
    },
  });
  const removeAppliance = useMutation({
    mutationFn: (v: { unitId: string; applianceId: string }) =>
      api.units.removeAppliance(propertyId, v.unitId, v.applianceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: unitsKey }),
  });

  if (query.isLoading) return <Loading label="Loading units…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const units = query.data.units;

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 style={{ margin: 0 }}>Units</h3>
        <button type="button" className="secondary" onClick={() => setUnitEditing(null)}>
          Add unit
        </button>
      </div>

      {removeUnit.isError && <StatusMessage tone="err">{errorMessage(removeUnit.error)}</StatusMessage>}
      {removeAppliance.isError && (
        <StatusMessage tone="err">{errorMessage(removeAppliance.error)}</StatusMessage>
      )}

      {units.length === 0 ? (
        <p className="muted">No units on this property. Choose “Add unit” to create one.</p>
      ) : (
        units.map((u) => (
          <div key={u.id} className="card stack">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <strong>{u.label}</strong> <span className="badge">{labelize(u.status)}</span>
              </div>
              <div className="row">
                <button type="button" className="secondary" onClick={() => setUnitEditing(u)}>
                  Edit<span className="visually-hidden"> unit {u.label}</span>
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={removeUnit.isPending}
                  onClick={() => {
                    if (window.confirm(`Delete unit ${u.label}?`)) removeUnit.mutate(u);
                  }}
                >
                  Delete<span className="visually-hidden"> unit {u.label}</span>
                </button>
              </div>
            </div>

            <p className="muted" style={{ margin: 0 }}>
              {u.beds} bd · {u.bathsX10 / 10} ba
              {u.sqft != null ? ` · ${u.sqft} sq ft` : ''} · Rent{' '}
              {u.marketRentCents != null ? formatCents(u.marketRentCents) : '—'} · Deposit{' '}
              {u.depositCents != null ? formatCents(u.depositCents) : '—'} · AB 1482{' '}
              {u.effectiveExempt ? 'exempt' : labelize(u.ab1482Status)}
            </p>

            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h4 style={{ margin: 0 }}>Appliances</h4>
              <button
                type="button"
                className="secondary"
                onClick={() => setApplianceEditing({ unitId: u.id, appliance: null })}
              >
                Add appliance<span className="visually-hidden"> to unit {u.label}</span>
              </button>
            </div>

            {u.appliances.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                No appliances recorded.
              </p>
            ) : (
              <div className="table-wrap">
                <table>
                  <caption className="visually-hidden">Appliances in unit {u.label}</caption>
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Make / model</th>
                      <th scope="col">Condition</th>
                      <th scope="col">Habitability</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.appliances.map((a) => (
                      <tr key={a.id}>
                        <td>{labelize(a.category)}</td>
                        <td>{[a.make, a.model].filter(Boolean).join(' ') || '—'}</td>
                        <td>{labelize(a.condition)}</td>
                        <td>{a.isHabitabilityRequired ? 'Required' : '—'}</td>
                        <td>
                          <div className="row">
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setApplianceEditing({ unitId: u.id, appliance: a })}
                            >
                              Edit<span className="visually-hidden"> {labelize(a.category)}</span>
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              disabled={removeAppliance.isPending}
                              onClick={() => {
                                if (window.confirm(`Delete ${labelize(a.category)}?`))
                                  removeAppliance.mutate({ unitId: u.id, applianceId: a.id });
                              }}
                            >
                              Delete<span className="visually-hidden"> {labelize(a.category)}</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}

      {unitEditing !== undefined && (
        <UnitForm
          propertyId={propertyId}
          unit={unitEditing}
          onClose={() => setUnitEditing(undefined)}
        />
      )}
      {applianceEditing !== undefined && (
        <ApplianceForm
          propertyId={propertyId}
          unitId={applianceEditing.unitId}
          appliance={applianceEditing.appliance}
          onClose={() => setApplianceEditing(undefined)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Page.                                                              *
 * ------------------------------------------------------------------ */

export function AdminProperties() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PROPERTIES_KEY, queryFn: () => api.properties.list() });
  const ownersQuery = useQuery({ queryKey: OWNERS_KEY, queryFn: () => api.owners.list() });

  const [open, setOpen] = useState<string | null>(null);
  // `undefined` = closed; `null` = create; a property = edit.
  const [editing, setEditing] = useState<PropertyDTO | null | undefined>(undefined);

  const remove = useMutation({
    mutationFn: (p: PropertyDTO) => api.properties.remove(p.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROPERTIES_KEY }),
  });

  if (query.isLoading) return <Loading label="Loading properties…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const properties = query.data.properties;
  const ownerName = (id: string) =>
    ownersQuery.data?.owners.find((o) => o.id === id)?.name ?? '—';

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Properties</h1>
        <button type="button" onClick={() => setEditing(null)}>
          New property
        </button>
      </div>
      <p className="subtitle">Your portfolio. Select a property to manage its units and appliances.</p>

      {remove.isError && <StatusMessage tone="err">{errorMessage(remove.error)}</StatusMessage>}

      {properties.length === 0 ? (
        <p className="muted">No properties yet. Choose “New property” to add one.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }} className="stack">
          {properties.map((p) => (
            <li key={p.id} className="card">
              <div
                className="row"
                style={{ justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}
              >
                <button
                  type="button"
                  className="secondary"
                  aria-expanded={open === p.id}
                  onClick={() => setOpen(open === p.id ? null : p.id)}
                >
                  {p.name} — {p.line1}, {p.city}, {p.state}
                </button>
                <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
                  <span className="badge">{labelize(p.type)}</span>
                  <span className="muted">{ownerName(p.ownerId)}</span>
                  <span className="muted">
                    {p.unitCount ?? 0} unit{(p.unitCount ?? 0) === 1 ? '' : 's'}
                  </span>
                  <button type="button" className="secondary" onClick={() => setEditing(p)}>
                    Edit<span className="visually-hidden"> {p.name}</span>
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete property ${p.name}? This cannot be undone.`))
                        remove.mutate(p);
                    }}
                  >
                    Delete<span className="visually-hidden"> {p.name}</span>
                  </button>
                </div>
              </div>
              {open === p.id && (
                <div style={{ marginTop: '0.75rem' }}>
                  <UnitsPanel propertyId={p.id} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== undefined && (
        <PropertyForm property={editing} onClose={() => setEditing(undefined)} />
      )}
    </>
  );
}
